/**
 * Hook to start a client data export and optionally poll for completion.
 * Shows an immediate "export started" notification; if the user stays, shows a second
 * notification with download link when the job completes. The backend also emails
 * the request user when the job finishes.
 */
import type React from "react";
import { createElement, useCallback, useEffect, useRef, useState } from "react";
import { hideNotification, showNotification } from "@mantine/notifications";
import {
  startClientExport,
  getClientExportStatus,
} from "@/utils/api/clientExportApi";

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

/** Position for all client-export notifications (only this hook). */
const EXPORT_NOTIFICATION_POSITION = "bottom-center" as const;

export const CLIENT_EXPORT_NOTIFICATIONS = {
  STARTED_TITLE: "Export started",
  STARTED_MESSAGE: "You can navigate away — we'll email you when it's ready.",
  COMPLETE_TITLE: "Export complete",
  COMPLETE_MESSAGE: "Your export is ready. Click to download.",
  FAILED_TITLE: "Export failed",
  TIMEOUT_MESSAGE:
    "Export is taking longer than expected; we'll email you when it's ready.",
} as const;

export interface UseClientExportOptions {
  /** Client ID when a tax pro is exporting a client's data; omit when client exports own data. */
  clientId?: string;
  /** Available tax years for the export menu (from profile store or organizer list). */
  availableYears: number[];
}

export function useClientExport(options: UseClientExportOptions) {
  const { clientId } = options;
  const [isExporting, setIsExporting] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => clearPolling(), [clearPolling]);

  const startExport = useCallback(
    async (taxYear: number | "all") => {
      if (isExporting) return;
      setIsExporting(true);
      setLastError(null);

      const body: Parameters<typeof startClientExport>[0] = {};
      if (clientId !== undefined) body.clientId = clientId;
      if (taxYear !== "all") body.taxYear = taxYear;

      try {
        const { jobId } = await startClientExport(body);

        showNotification({
          title: CLIENT_EXPORT_NOTIFICATIONS.STARTED_TITLE,
          message: CLIENT_EXPORT_NOTIFICATIONS.STARTED_MESSAGE,
          color: "blue",
          position: EXPORT_NOTIFICATION_POSITION,
        });

        const startTime = Date.now();

        const checkStatus = async () => {
          try {
            const status = await getClientExportStatus({ jobId });
            if (status.status === "completed") {
              clearPolling();
              setIsExporting(false);
              if (status.downloadUrl && !status.expired) {
                const completeId = "client-export-complete";
                const downloadUrl = status.downloadUrl;
                showNotification({
                  id: completeId,
                  title: CLIENT_EXPORT_NOTIFICATIONS.COMPLETE_TITLE,
                  message: createElement(
                    "span",
                    {
                      role: "button",
                      tabIndex: 0,
                      onClick: () => {
                        window.open(downloadUrl, "_blank");
                        hideNotification(completeId);
                      },
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          window.open(downloadUrl, "_blank");
                          hideNotification(completeId);
                        }
                      },
                      style: {
                        cursor: "pointer",
                        textDecoration: "underline",
                      },
                    },
                    CLIENT_EXPORT_NOTIFICATIONS.COMPLETE_MESSAGE
                  ),
                  color: "green",
                  autoClose: false,
                  withCloseButton: true,
                  position: EXPORT_NOTIFICATION_POSITION,
                });
              }
              return;
            }
            if (status.status === "failed") {
              clearPolling();
              setIsExporting(false);
              showNotification({
                title: CLIENT_EXPORT_NOTIFICATIONS.FAILED_TITLE,
                message:
                  status.message ??
                  "The export failed. You will receive an email if we have more details.",
                color: "red",
                position: EXPORT_NOTIFICATION_POSITION,
              });
              setLastError(status.message ?? "Export failed");
              return;
            }
          } catch (err) {
            // Don't stop polling on transient errors
          }

          if (Date.now() - startTime >= POLL_TIMEOUT_MS) {
            clearPolling();
            setIsExporting(false);
            showNotification({
              title: CLIENT_EXPORT_NOTIFICATIONS.STARTED_TITLE,
              message: CLIENT_EXPORT_NOTIFICATIONS.TIMEOUT_MESSAGE,
              color: "blue",
              position: EXPORT_NOTIFICATION_POSITION,
            });
          }
        };

        checkStatus();
        pollIntervalRef.current = setInterval(checkStatus, POLL_INTERVAL_MS);
        pollTimeoutRef.current = setTimeout(() => {
          clearPolling();
          setIsExporting(false);
          showNotification({
            title: CLIENT_EXPORT_NOTIFICATIONS.STARTED_TITLE,
            message: CLIENT_EXPORT_NOTIFICATIONS.TIMEOUT_MESSAGE,
            color: "blue",
            position: EXPORT_NOTIFICATION_POSITION,
          });
        }, POLL_TIMEOUT_MS);
      } catch (err) {
        setIsExporting(false);
        const message =
          err instanceof Error ? err.message : "Failed to start export";
        setLastError(message);
        showNotification({
          title: CLIENT_EXPORT_NOTIFICATIONS.FAILED_TITLE,
          message,
          color: "red",
          position: EXPORT_NOTIFICATION_POSITION,
        });
      }
    },
    [clientId, isExporting, clearPolling]
  );

  return { startExport, isExporting, lastError };
}
